import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";

import { Stack } from "@mui/material";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import Typography from "@mui/material/Typography";

import { Dialog } from "primereact/dialog";

import { WorkflowTab } from "./WorkflowDetail/WorkflowTab";
import { SubstationTab } from "./WorkflowDetail/SubstationTab";
import { MeasurementsTab } from "./WorkflowDetail/MeasurementsTab";

import { UTP2GroupedConditions, CableNaming } from "../shared/enums/UTP2";
import { ProgressButton } from "../shared/ProgressButton";

import type { WorkflowReducer } from "./workflowReducer";
import { CancelButton } from "../shared/CancelButton";
import { MeasurementsOrderTab } from "./WorkflowDetail/MeasurementsOrderTab";

interface Props {
  workflowReducer: WorkflowReducer;
  editMode?: boolean;
  isNewWorkflow?: boolean;
  isLoading?: boolean;
  onSave?: () => void;
  saveLoading?: boolean;
  onCancel?: () => void;
  isDialog?: boolean;
  dialogOpen?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
    },
    stepper: {
      backgroundColor: "transparent",
    },
    actionButtons: {
      display: "flex",
      justifyContent: "flex-end",
    },
    button: {
      marginRight: theme.spacing(1),
    },
    content: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    icon: {
      fontSize: "1em",
      marginLeft: theme.spacing(1),
      color: theme.palette.error.main,
    },
    dialogContent: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      height: "70vh",
    },
  }),
);

export function WorkflowEditUI(props: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { trigger } = useFormContext();

  const [activeStep, setActiveStep] = React.useState(0);
  const [companyName, setCompanyName] = React.useState<string | undefined>(undefined);
  const [siteName, setSiteName] = React.useState<string | undefined>(undefined);
  const hasResetNewWorkflowMeasurementOrder = React.useRef(false);

  useEffect(() => {
    if (props.isNewWorkflow && props.dialogOpen) {
      hasResetNewWorkflowMeasurementOrder.current = false;
    }
  }, [props.dialogOpen, props.isNewWorkflow]);

  function handleWorkflowFieldChange(field: string, value: string) {
    props.workflowReducer.updateWorkflowDetails({
      type: "updateWorkflowField",
      key: field,
      value: value,
    });
  }

  function handleConditionToggle(condition: UTP2GroupedConditions) {
    props.workflowReducer.updateWorkflowDetails({
      type: "toggleCondition",
      condition: condition,
    });
  }

  function handleCableNamingChange(value: CableNaming) {
    props.workflowReducer.updateWorkflowDetails({
      type: "updateCableNaming",
      value: value,
    });
  }

  function handleCompanyNameChange(companyName: string | undefined) {
    setCompanyName(companyName);
  }

  function handleSiteNameChange(siteName: string | undefined) {
    setSiteName(siteName);
  }

  const headers = [
    t("workflows.workflow"),
    t("substations.substation"),
    t("workflows.measurements"),
    t("workflows.measurementsOrder"),
  ];

  function getStepContent(step: number) {
    const content = [
      <WorkflowTab
        workflow={props.workflowReducer.state.workflow}
        editMode={props.editMode}
        onWorkflowFieldChange={handleWorkflowFieldChange}
        isNewWorkflow={props.isNewWorkflow}
      />,
      <SubstationTab
        workflowReducer={props.workflowReducer}
        editMode={props.editMode}
        onConditionToggle={handleConditionToggle}
        onCableNamingChange={handleCableNamingChange}
        onCompanyNameChange={handleCompanyNameChange}
        onSiteNameChange={handleSiteNameChange}
        companyName={companyName}
        siteName={siteName}
      />,
      <MeasurementsTab workflowReducer={props.workflowReducer} editMode={props.editMode} />,
      <MeasurementsOrderTab workflowReducer={props.workflowReducer} editMode={props.editMode} />,
    ];

    return content[step];
  }

  async function checkStepErrors() {
    if (activeStep === 0) {
      return await trigger("workflowTab");
    } else if (activeStep === 1) {
      return await trigger("substationTab");
    } else if (activeStep === 2) {
      return await trigger("measurementsTab");
    } else if (activeStep === 3) {
      return await trigger("measurementsOrderTab");
    } else return false;
  }

  const handleNext = () => {
    checkStepErrors().then((isValid) => {
      if (isValid && props.isNewWorkflow && activeStep === 2 && !hasResetNewWorkflowMeasurementOrder.current) {
        props.workflowReducer.resetMeasurementsOrder();
        hasResetNewWorkflowMeasurementOrder.current = true;
      }

      isValid && setActiveStep((prevActiveStep) => prevActiveStep + 1);
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCancelButtonClick = () => {
    setCompanyName(undefined);
    setSiteName(undefined);
    setActiveStep(0);
    props.onCancel?.();
  };

  const workflowStepper = (
    <>
      <Stepper activeStep={activeStep} sx={{ marginY: 2 }}>
        {headers.map((label) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: { optional?: React.ReactNode } = {};

          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {getStepContent(activeStep)}
    </>
  );

  const actionButtons = (
    <>
      <Button disabled={activeStep === 0} onClick={handleBack}>
        {t("common.back")}
      </Button>
      {activeStep === headers.length - 1 ? (
        <ProgressButton
          startIcon={<SaveIcon />}
          onClick={props.onSave}
          disabled={props.saveLoading}
          loading={props.saveLoading || false}
        >
          {" "}
          {t("common.save")}{" "}
        </ProgressButton>
      ) : (
        <Button variant="contained" color="primary" onClick={handleNext}>
          {t("common.next")}
        </Button>
      )}
      <CancelButton onClick={handleCancelButtonClick} className="m1-1" />
    </>
  );

  const header = <Typography variant="h5" component="p">{`${t("workflows.createWorkflow")} `}</Typography>;

  const footer = <div className="d-flex flex-row align-items-center justify-content-end">{actionButtons}</div>;

  return props.isDialog ? (
    <Dialog
      header={header}
      footer={footer}
      visible={props.dialogOpen}
      onHide={handleCancelButtonClick}
      breakpoints={{ "1960px": "85vw", "1300px": "90vw" }}
      style={{ width: "70vw" }}
    >
      <div className={classes.dialogContent}>{workflowStepper}</div>
    </Dialog>
  ) : (
    <>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {actionButtons}
      </Stack>

      {workflowStepper}
    </>
  );
}
